

import { Component, Vue, Prop } from "vue-property-decorator"

import { CurrentUser } from "@/types/user"
import { EditDataPermission, EditDataPermissionTitles, PermissionData } from "@/types/permission"
import { DataSource } from "@/types"

import SourceEdit from "@/components/Config/Sources/SourceEdit.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import SourceSettings from "./Sources/SourceSettings.vue"
import ViewFolders from "../New/Folders/ViewFolders.vue"
import { FolderType, Folder } from "@/types/folder"
import api from "@/services/api"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

@Component({
	components: {
		"source-edit": SourceEdit,
		"source-setting": SourceSettings,
		"view-folders": ViewFolders,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class ConfigMenu extends Vue {
	@Prop({ default: null })
	currentUser!: CurrentUser | null

	@Prop({ default: null })
	public dataSources!: DataSource[] | null

	@Prop({ default: [] })
	public dataSourceFolders!: Folder[]

	@Prop({ default: () => null })
	public tenantFeatures!: { [key: string]: boolean }

	dataSourceFilter: string | null = null
	newSource: DataSource | null = null

	isMounted: boolean = false

	get permissionSections (): PermissionData[] {
		if (!this.currentUser || !this.currentUser.editData) {
			return []
		}

		if (!this.currentUser.editDataPermissions || this.currentUser.editDataPermissions.length === 0) {
			return EditDataPermissionTitles
		}

		return this.currentUser.editDataPermissions!
			.map(p => EditDataPermissionTitles.find(permission => permission.id === p)!)
	}

	get displaySections (): PermissionData[] {
		const editDataSections = this.permissionSections.filter(p => !!p && !p.hideAsRoute && (!p.tenantFeature || (this.tenantFeatures && this.tenantFeatures[p.tenantFeature!])))

		editDataSections.push({
			id: "AuditLog",
			title: "Change History",
			path: "history"
		})

		return this.currentUser?.isAdmin
			? [
				...editDataSections,
				{
					id: "admin",
					title: "Admin Settings",
					path: "admin"
				}
			]
			: editDataSections
	}

	get activeRoute (): string | null {
		return this.$route ? this.$route.path : null
	}

	get dataSourceListFiltered (): DataSource[] | null {
		if (!this.dataSourceFilter || !this.dataSources) {
			return this.dataSources
		}
		const lowerFilter = this.dataSourceFilter.toLowerCase()
		return this.dataSources.filter(d => d.title.toLowerCase().includes(lowerFilter))
	}

	get canCreateSource (): boolean {
		return !!this.currentUser &&
			!!this.currentUser.editData &&
			(!this.currentUser.editDataPermissions ||
			this.currentUser.editDataPermissions.length === 0 ||
			this.currentUser.editDataPermissions.includes(EditDataPermission.CreateDataSource))
	}

	get canEditSourceSettings (): boolean {
		return !!this.currentUser &&
			!!this.currentUser.editData &&
			(!this.currentUser.editDataPermissions ||
			this.currentUser.editDataPermissions.length === 0 ||
			this.currentUser.editDataPermissions.includes(EditDataPermission.DataSourceSettings))
	}

	get canViewSourceList (): boolean {
		return !!this.currentUser &&
			!!this.currentUser.editData &&
			(!this.currentUser.editDataPermissions ||
			this.currentUser.editDataPermissions.length === 0 ||
			this.currentUser.editDataPermissions.includes(EditDataPermission.DataSourceList))
	}

	get dataSourceDisplay (): boolean {
		return this.canCreateSource || this.canViewSourceList || this.canEditSourceSettings
	}

	get showItemCount () {
		return this.tenantFeatures && this.tenantFeatures[TenantFeatures.DataSourceListItemCount]
	}

	private get formatEnabled (): boolean {
		return this.tenantFeatures && this.tenantFeatures[TenantFeatures.EmailFormats]
	}

	mounted () {
		if (this.$route && this.$route.name === "configuration") {
			if (this.displaySections.length > 1) { // Ignore Change History item
				this.goTo(this.displaySections[0])
			} else if (this.canViewSourceList &&
				this.dataSources &&
				this.dataSources.length > 0) {
				this.goToDataSource(this.dataSources[0])
			}
		}

		this.isMounted = true
	}

	goTo (permission: PermissionData) {
		if (permission.path) {
			this.$router.push({ name: permission.title })
		}
	}

	createNewSource () {
		this.newSource = {
			id: null,
			title: "",
			folder: "",
			definitionId: null,
			includedLists: [],
			editPermissions: [],
			itemCount: 0
		}
	}

	goToDataSource (dataSource: DataSource) {
		this.$router.push({ path: `/configuration/data-source/${dataSource.id}` })
	}

	goToDataSourceSettings (): void {
		this.$router.push({ name: "Data Source Settings" })
	}

	get dataSourceFoldersFeatureIsEnabled (): boolean {
		return this.tenantFeatures && this.tenantFeatures[TenantFeatures.DataSourceFolders]
	}

	get activeDataSourceId (): string | null {
		if (this.$route.name === "Data Source List" && !!this.$route.params.dataSourceId) {
			return this.dataSources?.some(ds => ds.id === this.$route.params.dataSourceId)
				? this.$route.params.dataSourceId
				: null
		} else {
			return null
		}
	}
}
